// src/components/controlPanel/CalendarDayCell.jsx
import { useMemo } from "react";

export default function CalendarDayCell({
  date,
  defaultHours,
  override,
  isSelected,
  isToday,
  isCurrentMonth,
  onSelect,
  onShiftSelect,
}) {
  const dayNumber = date.getDate();

  const { bgColor, textColor, hoursPreview, hasOverride } = useMemo(() => {
    const hasOvr = !!override;
    let isClosed = false;
    let openTime = null;
    let closeTime = null;

    if (hasOvr) {
      isClosed = override.is_closed;
      openTime = override.open_time;
      closeTime = override.close_time;
    } else if (defaultHours) {
      isClosed = defaultHours.closed || !defaultHours.open || !defaultHours.close;
      openTime = defaultHours.open;
      closeTime = defaultHours.close;
    } else {
      isClosed = true;
    }

    let bg, text, preview;

    if (isClosed) {
      if (hasOvr) {
        bg = "bg-orange-100 hover:bg-orange-200";
        text = "text-orange-800";
      } else {
        bg = "bg-gray-100 hover:bg-gray-200";
        text = "text-gray-500";
      }
      preview = "Closed";
    } else {
      if (hasOvr) {
        bg = "bg-teal-100 hover:bg-teal-200";
        text = "text-teal-800";
      } else {
        bg = "bg-green-50 hover:bg-green-100";
        text = "text-green-800";
      }
      const formatTime = (t) => {
        if (!t) return "?";
        const [h, m] = t.split(":");
        const hour = parseInt(h, 10);
        return hour > 12 ? `${hour - 12}p` : `${hour}a`;
      };
      preview = `${formatTime(openTime)}-${formatTime(closeTime)}`;
    }

    return {
      bgColor: bg,
      textColor: text,
      hoursPreview: preview,
      hasOverride: hasOvr,
    };
  }, [date, defaultHours, override]);

  const handleClick = (e) => {
    if (e.shiftKey && onShiftSelect) {
      onShiftSelect(date);
    } else {
      onSelect(date);
    }
  };

  if (!isCurrentMonth) {
    return (
      <div className="min-h-[44px] h-12 sm:h-16 bg-gray-50 border border-gray-100 rounded" />
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`
        min-h-[44px] h-12 sm:h-16 w-full rounded border transition-all cursor-pointer
        flex flex-col items-center justify-center gap-0.5 relative
        ${bgColor}
        ${isSelected ? "ring-2 ring-teal-500 ring-offset-1" : "border-gray-200"}
        ${isToday ? "ring-2 ring-blue-400" : ""}
      `}
    >
      <span className={`text-xs sm:text-sm font-medium ${textColor}`}>
        {dayNumber}
      </span>
      <span className={`text-[10px] sm:text-xs ${textColor} opacity-75`}>
        {hoursPreview}
      </span>
      {hasOverride && (
        <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-teal-500 rounded-full" />
      )}
    </button>
  );
}
